<template>
  <UnreadNewsPopup></UnreadNewsPopup>

  <MonthSelectModal :currentDate=currentDate :show-modal="showMonthModal" v-on:monthPicked="modalMonthPicked"
                    v-on:modalClosed="monthModalClosed"></MonthSelectModal>

  <BuddySelectBar :interactable="true" @buddiesLoaded="onBuddiesLoaded"
                  @buddyChanged="onBuddySelected"></BuddySelectBar>

  <div class="container">

    <div class="row" v-if="loadingCalendarData">
      <div class="col-12 text-center">
        <div class="alert alert-primary" role="alert">
          Activiteiten ophalen.
        </div>
        <div class="d-flex justify-content-center">
          <div class="spinner-border" role="status">
            <span class="sr-only">Laden...</span>
          </div>
        </div>
      </div>
    </div>
    <div class="row" v-else-if="overdueActivities.length > 0">
      <div class="col-12">
        <RescheduleActivities :activities="overdueActivities"
                              @rescheduled="onOverdueActivitiesRescheduled"></RescheduleActivities>

        <v-modal v-if="showOverdueActivitiesDialog" @close="showOverdueActivitiesDialog = false">
          <template v-slot:container>
            <div class="modal-container expired-activities-modal">
              <div class="modal-header">
                <h3>Verlopen activiteiten</h3>
              </div>
              <div class="modal-body">
                <p>
                  Er stonden activiteiten gepland die verlopen zijn.<br/>
                  Wat wil je met de verlopen activiteiten doen?
                </p>
                <ul>
                  <li><strong>Herplan</strong> de verlopen activiteiten op een ander moment.</li>
                  <li><strong>Annuleer</strong> de verlopen activiteiten.</li>
                </ul>
              </div>
              <div class="modal-footer">
                <div class="expired-activities-dialog__buttons">
                  <button class="btn btn-sandpiper" type="button" :disabled="cancellingOverdueActivities"
                          @click="showOverdueActivitiesDialog = false">
                    Activiteiten herplannen
                  </button>
                  <button class="btn btn-sandpiper cancel-activities" type="button"
                          :disabled="cancellingOverdueActivities"
                          @click="cancelOverdueActivities">
                    Activiteiten annuleren
                  </button>
                </div>
              </div>
            </div>
          </template>
        </v-modal>
      </div>
    </div>
    <div class="row agenda-cards" v-else>
      <div class="col-lg-4">
        <AgendaNextActivity :next-activity="nextActivity" :buddyId="selectedBuddy?.id"
                            v-on:startActivityClicked="startActivityClicked"
                            :showFinishYearButton="false"></AgendaNextActivity>
      </div>

      <div class="col-lg-4" v-if="showAgendaContainer">
        <AgendaContainer :activities="activities" :events="events" :currentDate="currentDate"
                         v-on:openListViewClicked="openListViewClicked" v-on:dateChanged="dateChanged"
                         v-on:openMonthModalClicked="openMonthModal"></AgendaContainer>
      </div>
      <div class="col-lg-4" v-if="!showAgendaContainer">
        <AgendaListViewContainer :activities="activities" :events="events" :currentDate="currentDate"
                                 @openGridViewClicked="openGridViewClicked" @dateChanged="dateChanged"
                                 @openMonthModalClicked="openMonthModal"
                                 @add-calendar-activity="addCalendarActivity"
                                 @add-calendar-event="addCalendarEvent"></AgendaListViewContainer>
      </div>
      <div class="col-lg-4 mb-5" v-if="showAgendaContainer">
        <AgendaActivityList :activities="getActivitiesForCurrentDate" :events="getEventsForCurrentDate"
                            :currentDate="currentDate" @add-calendar-activity="addCalendarActivity"
                            @add-calendar-event="addCalendarEvent"></AgendaActivityList>
      </div>
    </div>
  </div>
</template>

<script>
import AgendaContainer from "@/components/Agenda/AgendaContainer";
import AgendaListViewContainer from "@/components/Agenda/AgendaListViewContainer";
import AgendaNextActivity from "@/components/Agenda/AgendaNextActivity";
import AgendaActivityList from "@/components/Agenda/AgendaActivityList";
import BuddySelectBar from "@/components/Common/BuddySelectBar";
import MonthSelectModal from "@/components/Agenda/MonthSelectModal";
import { BuddyApi } from "sandpiper_api";
import log from "@/utils/logger";
import DateComparison from "@/mixins/DateComparison";
import DateFormatter from "@/mixins/DateFormatter";
import RescheduleActivities from "@/components/Activities/RescheduleActivities";
import QueryParamDateHelper from "@/mixins/QueryParamDateHelper";
import UnreadNewsPopup from "@/views/Agenda/UnreadNewsPopup.vue";

export default {
  name: "Agenda",
  components: {
    UnreadNewsPopup,
    RescheduleActivities,
    AgendaNextActivity,
    AgendaActivityList,
    AgendaContainer,
    AgendaListViewContainer,
    BuddySelectBar,
    MonthSelectModal
  },
  mixins: [ DateComparison, DateFormatter, QueryParamDateHelper ],
  inject: [ "doApiRequest" ],
  data()
  {
    const now = new Date();
    return {
      selectedBuddy: null,

      fetchingOverdueActivities: false,
      // Activities with planned date more than 1 day in the past
      overdueActivities: [],
      showOverdueActivitiesDialog: false,
      cancellingOverdueActivities: false,

      fetchingActivities: false,
      // Activities for the month of the currentDate
      activities: [],
      // Next planned activity
      nextActivity: null,

      fetchingCalendarEvents: false,
      // Events for the month of the currentDate
      events: [],

      currentDate: new Date(now),
      lastSelectedDate: new Date(now),
      showMonthModal: false,
      showAgendaContainer: true,
    }
  },
  computed: {
    getCurrentMonthDateRange()
    {
      return {
        beginDate: new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), 1, 0, 0, 0),
        endDate: new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 0, 23, 59, 59)
      };
    },
    getActivitiesForCurrentDate()
    {
      return this.activities.filter(activity => this.isSameDay(this.currentDate, activity.plannedDate));
    },
    getEventsForCurrentDate()
    {
      return this.events.filter(event => this.isSameDay(this.currentDate, event.date));
    },
    loadingCalendarData()
    {
      return this.fetchingOverdueActivities || this.fetchingActivities || this.fetchingCalendarEvents;
    }
  },
  methods: {
    onBuddiesLoaded(buddies)
    {
      log.info("Buddies loaded for activity page: %o", buddies);
      if (buddies.length === 0)
      {
        this.$router.push({ name: "create-buddy", query: { referer: this.$route.fullPath } });
      }
    },
    onBuddySelected(buddy)
    {
      log.info("Buddy selected for activity page: %o", buddy);
      if (this.selectedBuddy !== buddy)
      {
        this.selectedBuddy = buddy;
        this.checkOverdueActivities();
      }
    },
    onOverdueActivitiesRescheduled()
    {
      this.checkOverdueActivities();
    },
    async checkOverdueActivities()
    {
      await this.fetchOverdueBuddyActivities();

      if (this.overdueActivities.length === 0)
      {
        this.loadBuddyCalendarData();
      }
      else
      {
        this.showOverdueActivitiesDialog = true;
      }
    },
    loadBuddyCalendarData()
    {
      this.fetchNextActivity();
      this.fetchBuddyActivities();
      this.fetchCalendarEvents();
    },

    async fetchNextActivity()
    {
      if (!this.selectedBuddy || this.fetchingNextActivity) return;

      try
      {
        this.fetchingNextActivity = true;
        this.nextActivity = await this.doApiRequest(
            () => new BuddyApi().getNextActivityForBuddy(this.selectedBuddy.id));
      }
      catch (e)
      {
        this.nextActivity = null;
        // TODO - handle error
      }
      finally
      {
        this.fetchingNextActivity = false;
      }
    },
    async fetchOverdueBuddyActivities()
    {
      if (!this.selectedBuddy || this.fetchingOverdueActivities) return;

      try
      {
        this.fetchingOverdueActivities = true;
        this.overdueActivities = [];
        this.overdueActivities = await this.doApiRequest(
            () => new BuddyApi().getOverdueBuddyActivities(this.selectedBuddy.id));
      }
      catch (e)
      {
        // TODO - handle error
      }
      finally
      {
        this.fetchingOverdueActivities = false;
      }
    },
    async cancelOverdueActivities()
    {
      if (this.cancellingOverdueActivities) return;

      try
      {
        this.cancellingOverdueActivities = true;

        this.nextActivity = await this.doApiRequest(
            () => new BuddyApi().cancelOverdueBuddyActivities(this.selectedBuddy.id));

        await this.checkOverdueActivities();
      }
      catch (e)
      {
        // TODO - handle error
      }
      finally
      {
        this.cancellingOverdueActivities = false;
      }
    },
    async fetchBuddyActivities()
    {
      if (!this.selectedBuddy || this.fetchingActivities) return;

      try
      {
        this.fetchingActivities = true;
        this.activities = [];
        this.activities = await this.doApiRequest(() => new BuddyApi().getBuddyActivities(this.selectedBuddy.id, {
          ...this.getCurrentMonthDateRange,
          questId: null
        }));
      }
      catch (e)
      {
        // TODO - handle error
      }
      finally
      {
        this.fetchingActivities = false;
      }
    },
    async fetchCalendarEvents()
    {
      if (!this.selectedBuddy || this.fetchingCalendarEvents) return;

      try
      {
        this.fetchingCalendarEvents = true;

        // Note - bug with javascript openapi generator. Date doesn't get formatted correctly
        // !important! - make sure not to edit the computed object!
        let dateRange = Object.assign({}, this.getCurrentMonthDateRange);
        dateRange.beginDate = this.apiDateFormat(dateRange.beginDate);
        dateRange.endDate = this.apiDateFormat(dateRange.endDate);

        this.events = [];
        const response = await this.doApiRequest(() => new BuddyApi().getBuddyCalendarEvents(this.selectedBuddy.id, {
          ...dateRange,
          page: 0,
          count: 50
        }));

        this.events = response.calendarEvents;
      }
      catch (e)
      {
        // TODO - handle error
      }
      finally
      {
        this.fetchingCalendarEvents = false;
      }
    },
    startActivityClicked()
    {
      console.log(this.selectedBuddy)
      this.$router.push({
        name: 'student-platform', params: {
          id: this.selectedBuddy.id
        }
      });
    },
    dateChanged(newDate)
    {
      //for now we just set the timestamp to the new date
      //creating a new Date because otherwise Vue would not be able to notice the change
      let previousMonth = this.lastSelectedDate.getMonth();

      this.currentDate = new Date(newDate);
      log.info("Date updated from: %s\nto: %s", this.lastSelectedDate, this.currentDate);
      this.lastSelectedDate = new Date(this.currentDate);

      if (this.currentDate.getMonth() !== previousMonth)
      {
        this.fetchBuddyActivities();
        this.fetchCalendarEvents();
      }
    },
    openMonthModal()
    {
      this.showMonthModal = true;
    },
    modalMonthPicked(monthIndex)
    {
      this.dateChanged(new Date(this.currentDate.setMonth(monthIndex)));
    },
    monthModalClosed()
    {
      this.showMonthModal = false;
    },
    openListViewClicked()
    {
      this.showAgendaContainer = false;
    },
    openGridViewClicked()
    {
      this.showAgendaContainer = true;
    },
    addCalendarActivity()
    {
      this.$router.push({
        name: "plan-activity",
        query: {
          //initialDate: this.dateToQueryParam(this.currentDate)
        }
      });
    },
    addCalendarEvent()
    {
      this.$router.push({
        name: "event-add", query: {
          buddyId: this.selectedBuddy.id,
          //initialDate: this.dateToQueryParam(this.currentDate)
        }
      });
    }
  }
}
</script>

<style lang="scss" scoped>
.agenda-cards
{
  align-items: flex-start;
}

.card
{
  background-color: white;
  border-radius: 5px;
  box-shadow: $default-shadow;
  padding: 25px;
  margin-top: 25px;
  //min-width: 402px;
}

.expired-activities-modal
{
  max-width: 400px;

  .modal-body
  {
    padding: 35px 0 40px;
  }

  ul
  {
    padding: 0;
    list-style-position: inside;
  }
}

.expired-activities-dialog__buttons button
{
  width: 100%;

  &:not(:first-child)
  {
    margin-top: 15px;
  }
}
</style>